import React from 'react'
import Typography from 'aldoo-ra/Typography'
import { ChartBarIcon } from '@heroicons/react/24/outline'

const StatisticsTab = ({ release, timeRemaining, nextBatchCountdown, serverTimeOffset, elapsedSeconds }) => {
  const stats = release.deliveryStats || {}
  const emailsSent = release.emailsSent || 0

  // Progress calculation functions
  const progressPercentage = release.audienceSize > 0
    ? Math.min(100, Math.round((release.emailsSent || 0) / release.audienceSize * 100))
    : 0

  const displaySent = progressPercentage === 100 ? release.audienceSize : (release.emailsSent || 0)

  const formatUptime = () => {
    if (release.status !== 'running' || !release.startedAt) return null

    const seconds = elapsedSeconds || 0

    if (seconds < 60) return `${seconds}s`

    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60

    if (minutes < 60) return `${minutes}m ${secs}s`

    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return `${hours}h ${remainingMinutes}m`
  }

  const getCurrentRate = () => {
    if (release.status !== 'running') return null

    if (release._calculatedRate !== undefined && release._calculatedRate > 0) {
      return `${release._calculatedRate.toLocaleString()}/hr`
    }

    if (release.currentRate !== undefined && release.currentRate > 0) {
      return `${release.currentRate.toLocaleString()}/hr`
    }

    if (!release.startedAt) return 'Starting...'

    const hoursElapsed = (new Date() - new Date(release.startedAt)) / (1000 * 60 * 60)

    if ((hoursElapsed >= (5/60) || release.emailsSent >= 50) && release.emailsSent >= 10) {
      const currentRate = Math.round(release.emailsSent / hoursElapsed)
      return `${currentRate.toLocaleString()}/hr`
    }

    return 'Warm up...'
  }

  const formatCountdown = () => {
    if (!timeRemaining || timeRemaining <= 0) return 'Starting soon...'

    const totalSeconds = Math.floor(timeRemaining / 1000)
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (days > 0) return `${days}d ${hours}h ${minutes}m`
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`
    if (minutes > 0) return `${minutes}m ${seconds}s`
    return `${seconds}s`
  }

  if (!emailsSent || emailsSent === 0) {
    return (
      <div className="text-center py-12">
        <ChartBarIcon className="w-12 h-12 text-admin_text_secondary mx-auto mb-4" />
        <Typography variant="admin_body1_dim">
          No statistics available yet. Stats will appear as emails are sent and engagement events are received.
        </Typography>
      </div>
    )
  }

  const delivered = stats.delivered || 0
  const opened = stats.opened || 0
  const clicked = stats.clicked || 0
  const bounced = stats.bounced || 0
  const spam = stats.spamComplaint || 0
  const unsubscribed = stats.unsubscribed || 0

  const deliveredRate = emailsSent > 0 ? ((delivered / emailsSent) * 100).toFixed(1) : 0
  const openRate = emailsSent > 0 ? ((opened / emailsSent) * 100).toFixed(1) : 0
  const clickRate = emailsSent > 0 ? ((clicked / emailsSent) * 100).toFixed(1) : 0
  const bounceRate = emailsSent > 0 ? ((bounced / emailsSent) * 100).toFixed(1) : 0
  const spamRate = emailsSent > 0 ? ((spam / emailsSent) * 100).toFixed(1) : 0
  const unsubRate = emailsSent > 0 ? ((unsubscribed / emailsSent) * 100).toFixed(1) : 0

  return (
    <div className="space-y-6">
      {/* Statistics Funnel */}
      <div className="border border-admin_bordered rounded-lg p-4">
        <Typography variant="admin_label" className="mb-6">
          Overall Performance
        </Typography>

        {/* Funnel Visualization */}
        <div className="space-y-4">
        {/* Main funnel flow */}
        <div className="flex items-center justify-center gap-3">
          {/* Sent */}
          <div className="flex flex-col items-center min-w-[80px]">
            <Typography variant="admin_txt3_dim" className="mb-1">Sent</Typography>
            <Typography variant="admin_h3" className="font-bold text-admin_text">
              {emailsSent.toLocaleString()}
            </Typography>
            <Typography variant="admin_txt3_dim">Emails</Typography>
          </div>

          <div className="flex items-center">
            <svg width="40" height="24" viewBox="0 0 40 24" className="text-admin_bordered">
              <path d="M0 12 L35 12 M35 12 L28 6 M35 12 L28 18" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          </div>

          {/* Delivered */}
          <div className="flex flex-col items-center min-w-[80px]">
            <Typography variant="admin_txt3_dim" className="mb-1">Delivered</Typography>
            <Typography variant="admin_h3" className="font-bold text-admin_success">
              {delivered.toLocaleString()}
            </Typography>
            <Typography variant="admin_txt3_dim" className="text-admin_success">
              {deliveredRate}%
            </Typography>
          </div>

          <div className="flex items-center">
            <svg width="40" height="24" viewBox="0 0 40 24" className="text-admin_bordered">
              <path d="M0 12 L35 12 M35 12 L28 6 M35 12 L28 18" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          </div>

          {/* Opened */}
          <div className="flex flex-col items-center min-w-[80px]">
            <Typography variant="admin_txt3_dim" className="mb-1">Opened</Typography>
            <Typography variant="admin_h3" className="font-bold text-admin_primary">
              {opened.toLocaleString()}
            </Typography>
            <Typography variant="admin_txt3_dim" className="text-admin_primary">
              {openRate}%
            </Typography>
          </div>

          <div className="flex items-center">
            <svg width="40" height="24" viewBox="0 0 40 24" className="text-admin_bordered">
              <path d="M0 12 L35 12 M35 12 L28 6 M35 12 L28 18" stroke="currentColor" strokeWidth="2" fill="none"/>
            </svg>
          </div>

          {/* Clicked */}
          <div className="flex flex-col items-center min-w-[80px]">
            <Typography variant="admin_txt3_dim" className="mb-1">Clicked</Typography>
            <Typography variant="admin_h3" className="font-bold text-admin_info">
              {clicked.toLocaleString()}
            </Typography>
            <Typography variant="admin_txt3_dim" className="text-admin_info">
              {clickRate}%
            </Typography>
          </div>
        </div>

        {/* Negative outcomes below */}
        <div className="flex items-center justify-center pt-3 border-t border-admin_bordered">
          <div className="flex items-center justify-evenly w-full max-w-xl">
            <div className="flex flex-col items-center min-w-[100px]">
              <Typography variant="admin_txt3_dim" className="mb-1">Bounced</Typography>
              <Typography variant="admin_h3" className="font-bold text-admin_warning">
                {bounced.toLocaleString()}
              </Typography>
              <Typography variant="admin_txt3_dim" className="text-admin_warning">
                {bounceRate}%
              </Typography>
            </div>

            <div className="flex flex-col items-center min-w-[100px]">
              <Typography variant="admin_txt3_dim" className="mb-1">Spam</Typography>
              <Typography variant="admin_h3" className="font-bold text-admin_error">
                {spam.toLocaleString()}
              </Typography>
              <Typography variant="admin_txt3_dim" className="text-admin_error">
                {spamRate}%
              </Typography>
            </div>

            <div className="flex flex-col items-center min-w-[100px]">
              <Typography variant="admin_txt3_dim" className="mb-1">Unsubscribed</Typography>
              <Typography variant="admin_h3" className="font-bold text-admin_error">
                {unsubscribed.toLocaleString()}
              </Typography>
              <Typography variant="admin_txt3_dim" className="text-admin_error">
                {unsubRate}%
              </Typography>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Progress/Countdown Section */}
      {release.status === 'scheduled' ? (
        <div className="bg-admin_paper border border-admin_bordered rounded-lg p-6 text-center">
          <Typography variant="admin_txt2_dim" className="mb-2">Release starts in</Typography>
          <Typography variant="admin_h2" className="text-admin_primary font-bold">
            {formatCountdown()}
          </Typography>
        </div>
      ) : (
        <div className="bg-admin_paper border border-admin_bordered rounded-lg p-6">
          {release.status === 'running' && nextBatchCountdown !== null && nextBatchCountdown > 0 && (
            <div className="mb-4">
              <Typography variant="admin_txt2_dim" className="mb-1">
                Next batch will be sent in
              </Typography>
              <Typography variant="admin_h3" className="text-admin_primary">
                {(() => {
                  const totalSeconds = nextBatchCountdown
                  const days = Math.floor(totalSeconds / 86400)
                  const hours = Math.floor((totalSeconds % 86400) / 3600)
                  const minutes = Math.floor((totalSeconds % 3600) / 60)
                  const seconds = totalSeconds % 60

                  if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s`
                  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`
                  if (minutes > 0) return `${minutes}m ${seconds}s`
                  return `${seconds}s`
                })()}
              </Typography>
            </div>
          )}

          <div className="flex items-center justify-between mb-2">
            <Typography variant="admin_txt2_dim">Progress</Typography>
            <Typography variant="admin_txt2_dim">
              {displaySent.toLocaleString()} / {release.audienceSize.toLocaleString()} emails
            </Typography>
          </div>

          <div className="w-full bg-admin_bordered rounded-full h-3 mb-2">
            <div
              className="bg-admin_success h-3 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          <div className="flex items-center justify-between">
            <Typography variant="admin_txt2" className="text-admin_success font-semibold">
              {progressPercentage}%
            </Typography>
            {formatUptime() && (
              <Typography variant="admin_txt2_dim">
                Uptime: {formatUptime()}
              </Typography>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default React.memo(StatisticsTab)
